/**
 * ReleaseTooltip Component
 * 
 * Tooltip component for displaying detailed release information on hover,
 * following existing tooltip patterns and accessibility guidelines.
 */

import React from 'react'
import {
  Tooltip,
  Box,
  Typography,
  Chip,
  Divider,
  Link,
  TooltipProps,
  useTheme,
  styled
} from '@mui/material'
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UnreleasedIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material'
import { CategorizedRelease } from '../../types/release'
import {
  formatDisplayDate,
  getRelativeDateDescription,
  isOverdueRelease,
  isUpcomingRelease
} from '../../utils/releaseDateFiltering'
import { RELEASE_TIMELINE_COLORS, getCategoryColors } from '../../styles/releaseTimelineStyles'
import { getTooltipStyles, getTooltipProps } from '../../utils/tooltipStyles'

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const StyledTooltip = styled(({ className, isDarkMode, ...props }: TooltipProps & { isDarkMode: boolean }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, isDarkMode }) => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: isDarkMode ? '#1a1a1a !important' : '#ffffff !important',
    color: isDarkMode ? '#ffffff !important' : '#212121 !important',
    border: isDarkMode ? '1px solid #555555 !important' : '1px solid #d0d0d0 !important',
    boxShadow: isDarkMode
      ? '0 4px 20px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3) !important'
      : '0 4px 20px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1) !important',
    fontSize: '0.875rem !important',
    maxWidth: '360px !important',
    padding: '16px 20px !important',
    borderRadius: '8px !important',
    lineHeight: '1.5 !important',
    '& *': {
      color: 'inherit !important',
    }
  },
  '& .MuiTooltip-arrow': {
    color: isDarkMode ? '#1a1a1a !important' : '#ffffff !important',
    '&::before': {
      border: isDarkMode ? '1px solid #555555 !important' : '1px solid #d0d0d0 !important',
      backgroundColor: isDarkMode ? '#1a1a1a !important' : '#ffffff !important',
    }
  }
}))

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface ReleaseTooltipProps extends Omit<TooltipProps, 'title'> {
  /** Release data to display in tooltip */
  release: CategorizedRelease

  /** Whether to show detailed information */
  showDetails?: boolean

  /** Whether to show matched keywords */
  showKeywords?: boolean

  /** Custom tooltip placement */
  placement?: TooltipProps['placement']

  /** Track information for dual-track layout */
  trackInfo?: {
    trackNumber: number
    totalTracks: number
  }
}

// ============================================================================
// TOOLTIP CONTENT COMPONENT
// ============================================================================

const ReleaseTooltipContent: React.FC<{
  release: CategorizedRelease
  showDetails: boolean
  showKeywords: boolean
  trackInfo?: { trackNumber: number; totalTracks: number }
}> = ({ release, showDetails, showKeywords, trackInfo }) => {
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'
  const isReleased = release.released
  const isOverdue = isOverdueRelease(release)
  const isUpcoming = isUpcomingRelease(release)
  const categoryColors = release.category
    ? getCategoryColors(release.category, isDarkMode)
    : null
  
  return (
    <Box sx={{
      maxWidth: 340,
      p: 0,
      backgroundColor: 'transparent',
      color: isDarkMode ? '#ffffff' : '#212121'
    }}> {/* Increased from 260 to accommodate larger content */}
      {/* Release Name */}
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          mb: 1,
          color: isDarkMode ? '#ffffff' : '#212121',
          fontSize: '0.9375rem', // 15px for better readability
          lineHeight: 1.3
        }}
      >
        {release.displayName}
      </Typography>

      {/* Category and Status - Enhanced Row */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {release.category && (
          <Chip
            label={release.category}
            size="small"
            sx={{
              backgroundColor: categoryColors?.primary || '#1976d2',
              color: 'white',
              fontSize: '0.75rem', // Increased from 0.65rem for better readability
              height: 22, // Slightly taller for better proportion
              fontWeight: 500,
              borderRadius: '11px',
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
        )}

        <Chip
          icon={isReleased ? <CheckCircleIcon sx={{ fontSize: 12 }} /> : <UnreleasedIcon sx={{ fontSize: 12 }} />}
          label={isReleased ? 'Released' : 'Unreleased'}
          size="small"
          sx={{
            fontSize: '0.75rem', // Increased from 0.65rem for better readability
            height: 22, // Slightly taller for better proportion
            fontWeight: 500,
            borderRadius: '11px',
            backgroundColor: isReleased ? '#4caf50' : (isDarkMode ? '#424242' : '#f5f5f5'),
            color: isReleased ? 'white' : (isDarkMode ? '#e0e0e0' : '#424242'),
            '& .MuiChip-label': { px: 0.75 },
            '& .MuiChip-icon': { ml: 0.25, mr: -0.25 },
            ...(isOverdue && {
              backgroundColor: RELEASE_TIMELINE_COLORS.status.overdue,
              color: 'white'
            })
          }}
        />

        {isOverdue && (
          <Chip
            icon={<WarningIcon sx={{ fontSize: 12 }} />}
            label="Overdue"
            size="small"
            sx={{
              fontSize: '0.75rem', // Increased from 0.65rem for better readability
              height: 22, // Slightly taller for better proportion
              fontWeight: 500,
              borderRadius: '11px',
              backgroundColor: '#f44336',
              color: 'white',
              '& .MuiChip-label': { px: 0.75 },
              '& .MuiChip-icon': { ml: 0.25, mr: -0.25 }
            }}
          />
        )}
      </Box>
      
      {/* Release Date Information - Enhanced */}
      {release.releaseDate && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon sx={{
              fontSize: 16, // Slightly larger icon for better visibility
              color: isDarkMode ? '#e0e0e0' : '#424242'
            }} />
            <Typography variant="body2" sx={{
              fontSize: '0.8125rem', // Increased from 0.75rem for better readability
              fontWeight: 500,
              color: isDarkMode ? '#ffffff' : '#212121'
            }}>
              {formatDisplayDate(release.releaseDate)}
            </Typography>
            <Typography variant="caption" sx={{
              fontSize: '0.75rem', // Increased from 0.65rem for better readability
              color: isDarkMode ? '#e0e0e0' : '#424242'
            }}>
              ({getRelativeDateDescription(release.releaseDate)})
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Description - Enhanced */}
      {showDetails && release.displayDescription && (
        <>
          <Divider sx={{
            my: 1,
            borderColor: isDarkMode ? '#404040' : '#e0e0e0'
          }} />
          <Typography variant="body2" sx={{
            fontSize: '0.8125rem', // Increased from 0.7rem for better readability
            lineHeight: 1.4,
            color: isDarkMode ? '#e0e0e0' : '#424242',
            maxHeight: '4em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}>
            {release.displayDescription}
          </Typography>
        </>
      )}
      
      {/* Compact Footer Information */}
      {(showDetails || trackInfo || (showKeywords && release.matchedKeywords?.length)) && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>

            {/* Release ID - Clickable Link */}
            {showDetails && release.id && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  ID:
                </Typography>
                <Link
                  href={`https://redislabs.atlassian.net/projects/RED/versions/${release.id}/tab/release-report-all-issues`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: '0.75rem',
                    color: 'primary.main',
                    textDecoration: 'underline',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    '&:hover': {
                      color: 'primary.dark',
                      textDecoration: 'underline'
                    }
                  }}
                  aria-label={`Open JIRA release ${release.id} in new tab`}
                >
                  {release.id}
                  <OpenInNewIcon sx={{ fontSize: 10 }} />
                </Link>
              </Box>
            )}



            {/* Matched Keywords - Compact */}
            {showKeywords && release.matchedKeywords && release.matchedKeywords.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', mt: 0.25 }}>
                {release.matchedKeywords.slice(0, 3).map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.6875rem', // Increased from 0.55rem for better readability
                      height: 16, // Slightly taller for better proportion
                      color: 'text.secondary',
                      borderColor: 'divider',
                      '& .MuiChip-label': { px: 0.25 }
                    }}
                  />
                ))}
                {release.matchedKeywords.length > 3 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', alignSelf: 'center' }}>
                    +{release.matchedKeywords.length - 3} more
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

// ============================================================================
// MAIN TOOLTIP COMPONENT
// ============================================================================

const ReleaseTooltip: React.FC<ReleaseTooltipProps> = ({
  release,
  showDetails = true,
  showKeywords = false,
  placement = 'top',
  trackInfo,
  children,
  ...tooltipProps
}) => {
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'

  // Don't show tooltip if no meaningful content
  if (!release.displayName && !release.releaseDate && !release.category) {
    return <>{children}</>
  }

  return (
    <StyledTooltip
      isDarkMode={isDarkMode}
      title={
        <ReleaseTooltipContent
          release={release}
          showDetails={showDetails}
          showKeywords={showKeywords}
          trackInfo={trackInfo}
        />
      }
      placement={placement}
      {...getTooltipProps({ placement })}
      {...tooltipProps}
    >
      {children}
    </StyledTooltip>
  )
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

export default React.memo(ReleaseTooltip)

// ============================================================================
// COMPONENT DISPLAY NAME
// ============================================================================

ReleaseTooltip.displayName = 'ReleaseTooltip'

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Create a simple tooltip with just release name and date
 */
export const SimpleReleaseTooltip: React.FC<{
  release: CategorizedRelease
  children: React.ReactElement
}> = ({ release, children }) => (
  <ReleaseTooltip
    release={release}
    showDetails={false}
    showKeywords={false}
    placement="top"
  >
    {children}
  </ReleaseTooltip>
)

/**
 * Create a detailed tooltip with all information
 */
export const DetailedReleaseTooltip: React.FC<{
  release: CategorizedRelease
  children: React.ReactElement
}> = ({ release, children }) => (
  <ReleaseTooltip
    release={release}
    showDetails={true}
    showKeywords={true}
    placement="top"
  >
    {children}
  </ReleaseTooltip>
)
